import React, { useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { DateTime } from 'luxon';

import { Elements } from '@stripe/react-stripe-js';
import { Token } from '@stripe/stripe-js';

import { useQuery } from '@apollo/client/react';

import { Box, COLORS, Text } from '@clutter/clean';

import {
  DoorToDoor__BundleEnum,
  ErrorCodeEnum,
  GeocodeDocument,
  Lead__ServiceNeeds,
  OrderCancellationPolicyGroupDocument,
} from '@graphql/platform';

import {
  PaymentContextProvider,
  usePaymentContext,
} from '@root/components/checkout/payment_context';
import { setBookedFlag } from '@root/components/checkout/utilities/persistence';
import { DEFAULT_ELEMENTS_OPTIONS, getStripe } from '@root/initializers/stripe';
import {
  WTProvider,
  WT_VISITOR_TOKEN,
  flushEvents,
  useTrack,
} from '@root/initializers/wt';
import { ServiceEnum } from '@root/resources/types/service';
import { phone as phoneValidator } from '@root/resources/validator';
import { EventSchema } from '@root/resources/wt/types';
import { WWW_ROUTES } from '@root/root/routes';

import { useClientDataContext } from '@shared/client_data_context';
import { useCreateLead } from '@shared/lead_form';
import { MFAModal, MFAResult } from '@shared/mfa_modal';
import { PaymentMethodError } from '@shared/payment_method_error';

import { buildUrlWithQs } from '@utils/build_url_with_qs';
import { accountUrl as portalAccountUrl } from '@utils/environment_urls';
import {
  ErrorWithoutTypename,
  FormattedGQLError,
  GQLError,
} from '@utils/gql_errors';
import { useCartViewedFunnelEvent } from '@utils/hooks/funnel_events/use_cart_viewed_funnel_event';
import { useLeadCaptureViewedFunnelEvent } from '@utils/hooks/funnel_events/use_lead_capture_viewed_funnel_event';
import { useReservationCompletedFunnelEvent } from '@utils/hooks/funnel_events/use_reservation_completed_funnel_event';
import { useTrackFunnelEvents } from '@utils/hooks/funnel_events/use_track_funnel_event';
import { useOnMount } from '@utils/hooks/mount';
import { usePricingSetForZIP } from '@utils/hooks/pricing';
import { useBreakpoints } from '@utils/hooks/use_breakpoints';
import { getReferralCouponByService } from '@utils/referral';

import { MissingInformationModal } from './cart/missing_information_modal';
import { PaymentMethod } from './cart/payment_method';
import { PromoCodeInput } from './cart/promo_code_input';
import { useReservationSummaryProps } from './cart/use_reservation_summary_props';
import { useReserveSmartStorage } from './cart/use_reserve_smart_storage';
import { useStorageCheckoutContext } from './context';
import type { StorageCheckoutData } from './data';
import { CancellationPolicyModal } from './product_pages/challenger/cancellation_policy_modal';
import { ChallengerConfirmReservation } from './product_pages/challenger/confirm_reservation';
import { useChallengerLeadInfoFlowStepFlag } from './product_pages/challenger/hooks/use_challenger_flow_step_flags';
import { ChallengerLayout } from './product_pages/challenger/layout';
import type { SetPasswordLocationState } from './product_pages/challenger/set_password_page';
import { storageCalculatorItemsForReserve } from './product_pages/challenger/storage_calculator_items';
import { StorageCheckoutStep } from './types';
import { resolveAppointmentDateTime } from './utilities/appointment_date';
import { scrollToStep } from './utilities/scroll_animation';
import { useTrackBookingErrors } from './utilities/use_track_booking_errors';

const SERVICE_NEEDS = [Lead__ServiceNeeds.SmartStorage];
const RESERVE_BUTTON_LABEL = 'Reserve Smart Storage';
const PAYMENT_METHOD_ID = 'payment-method';
const PHONE_ID = 'phone-section';

const formatAddressLine = (
  address: StorageCheckoutData['address'],
): string | undefined => {
  if (!address) return undefined;
  const street = [address.street, address.aptsuite].filter(Boolean).join(', ');
  const cityStateZip =
    address.city && address.state
      ? `${address.city}, ${address.state}${address.zip ? ` ${address.zip}` : ''}`
      : undefined;
  return [street, cityStateZip].filter(Boolean).join(', ') || undefined;
};

const CheckoutCartContent = () => {
  const {
    flowState: { completeFlow, onChange, goToStep, prev, values },
    navigationState: { dispatch: navigationDispatch },
    pricingSummary,
  } = useStorageCheckoutContext();
  const {
    address,
    bundleKind,
    checkoutType,
    customerToken,
    coupon,
    commitment,
    dateScheduled,
    planSize,
    protectionPlan,
    zip,
    name,
    phone,
    email,
    defaultSource,
    disposalSelected,
  } = values;

  const history = useHistory();
  const location = useLocation();
  const track = useTrack();
  const { isDesktop } = useBreakpoints();
  const leadData = {
    name: name ?? '',
    phone: phone ?? '',
    email: email ?? '',
  };
  useCartViewedFunnelEvent();
  useLeadCaptureViewedFunnelEvent(true);
  useChallengerLeadInfoFlowStepFlag();

  const [showMFAModal, setShowMFAModal] = useState<boolean>(false);
  const [phoneError, setPhoneError] = useState<string | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [gqlError, setGqlError] = useState<ErrorWithoutTypename | null>(null);
  const [showCancellationModal, setShowCancellationModal] = useState(false);

  const editNavigationRef = useRef(false);

  const { tokenize, stripeToken, paymentMethodError, billingNameError } =
    usePaymentContext();

  const geocode =
    useQuery(GeocodeDocument, {
      variables: { zip: values.zip! },
      skip: !values.zip,
    }).data?.geocode ?? undefined;
  const { data: cancellationPolicyData } = useQuery(
    OrderCancellationPolicyGroupDocument,
    {
      variables: {
        visitorToken: WT_VISITOR_TOKEN,
        orderScheduledTime: values.dateScheduled?.fromTime,
      },
      skip: !geocode || geocode?.region?.name === 'Los Angeles',
    },
  );

  /**
   * Figma Confirm “Change” CTAs — return to the previous funnel step
   * (storage plan page), preserving query string.
   */
  const goToDetailPage = (step: StorageCheckoutStep) => {
    track({
      action: 'click',
      objectName: 'edit',
      value: step,
    });
    editNavigationRef.current = true;
    goToStep(step);
    navigationDispatch({ type: 'setTargetStep', payload: { step } });
    history.replace({
      pathname: WWW_ROUTES.PRODUCT_PAGE_STORAGE,
      search: location.search,
    });
  };

  useOnMount(() => {
    navigationDispatch({ type: 'viewCart' });
    const unsubscribe = history.listen((newLocation) => {
      // If a customer navigates back to the previous page via the back button, we want to ensure
      // that we track that action as a step transition (but not if they go to a
      // different, non-booking page which is treated as abandonment).
      if (
        WWW_ROUTES.PRODUCT_PAGE_STORAGE === newLocation.pathname &&
        !editNavigationRef.current
      ) {
        prev();
      }
      // This listener will only be called on navigation _after_ the page
      // unmounts, so we call this manually instead of returning as part of
      // effect cleanup
      unsubscribe();
    });
  });

  const {
    data: { referral },
  } = useClientDataContext();

  useOnMount(() => {
    if (!coupon) {
      onChange(
        'coupon',
        getReferralCouponByService(ServiceEnum.DoorToDoor, referral),
      );
    }
  });

  useTrackBookingErrors({
    phoneError,
    billingNameError,
    paymentMethodError,
    gqlError,
  });

  const pricingSet = usePricingSetForZIP(zip);

  const reservationSummaryProps = useReservationSummaryProps('challenger');

  const trackFunnelEvent = useTrackFunnelEvents();

  useOnMount(() => {
    trackFunnelEvent({
      schema: EventSchema.WWW__PhoneCaptureViewed,
      action: 'display',
      metadata: {},
    });
  });
  const trackReservationCompleted = useReservationCompletedFunnelEvent();
  const [reserveSmartStorage, quoteLoading, reserveQuoteReady, quoteError] =
    useReserveSmartStorage({
      address,
      bundleKind,
      commitment,
      coupon,
      checkoutType,
      dateScheduled,
      disposalSelected,
      estimatedItems: storageCalculatorItemsForReserve(
        values.storageCalculatorItems,
      ),
      lead: leadData,
      planSize,
      pricingSet,
      protectionPlan,
      zip,
      summary: reservationSummaryProps,
      pricingSummary,
      onReservationCompleted: (orderId) =>
        trackReservationCompleted({ orderId }),
    });
  const createLead = useCreateLead();

  const onMFAModalClose = ({ customerToken, leadToken }: MFAResult) => {
    setShowMFAModal(false);
    if (customerToken && leadToken) {
      track({ objectType: 'mfa_modal', action: 'submit' });
      onReserve({
        customerTokenOverride: customerToken,
        mfaLeadToken: leadToken,
      });
    } else {
      track({
        objectType: 'mfa_modal',
        action: 'click',
        label: 'close',
      });
    }
  };

  const onReserve = async ({
    customerTokenOverride = customerToken,
    mfaLeadToken,
    paymentRequired,
  }: {
    customerTokenOverride?: string;
    mfaLeadToken?: string;
    paymentRequired?: boolean;
  }) => {
    let hasBooked = false;
    setGqlError(null);
    setLoading(true);

    if (!mfaLeadToken) {
      track({
        action: 'click',
        objectName: 'reserve_button',
        label: paymentRequired ? RESERVE_BUTTON_LABEL : 'Save your reservation',
      });
    }

    try {
      let hasPhoneError = false;
      let hasCardError = false;
      if (phone && phoneValidator(phone)) {
        trackFunnelEvent({
          schema: EventSchema.WWW__PhoneCaptureCompleted,
          action: 'submit',
          metadata: { phone_number: phone },
        });
      } else {
        hasPhoneError = true;
        setPhoneError('Please enter a valid phone number');
      }

      let currentStripeToken: Token | undefined = stripeToken;
      if (!defaultSource) {
        currentStripeToken = await tokenize();
        if (!currentStripeToken) {
          hasCardError = true;
        }
      }

      if (hasPhoneError || hasCardError) {
        setTimeout(
          () =>
            scrollToStep(hasPhoneError ? PHONE_ID : PAYMENT_METHOD_ID, 'top'),
          10,
        );
        return;
      }

      let leadToken: string | undefined;
      if (!customerTokenOverride) {
        const lead = await createLead({
          serviceContext: ServiceEnum.DoorToDoor,
          leadAttributes: {
            name: leadData.name,
            phone: leadData.phone,
            email: leadData.email,
            serviceNeeds: SERVICE_NEEDS,
          },
          trackingParams: {
            zip_code: zip,
          },
        });
        leadToken = lead.token;
        onChange('leadToken', leadToken);
        if (lead.token && lead.id) {
          trackFunnelEvent({
            schema: EventSchema.WWW__LeadCaptureCompleted,
            action: 'submit',
            metadata: {
              name: leadData.name,
              email: leadData.email,
              lead_id: lead.id,
            },
          });
        }
      } else {
        leadToken = mfaLeadToken;
        trackFunnelEvent({
          schema: EventSchema.WWW__LeadCaptureCompleted,
          action: 'submit',
          metadata: {
            name: leadData.name,
            email: leadData.email,
            lead_id: leadToken ?? customerTokenOverride,
          },
        });
      }

      const metadata = await reserveSmartStorage(
        customerTokenOverride,
        leadToken,
        currentStripeToken,
      );

      if (metadata) {
        hasBooked = true;
        await completeFlow('book', metadata);
        setBookedFlag();
        flushEvents();
        const portalUrl = metadata.accountUrl
          ? buildUrlWithQs(metadata.accountUrl, {
              pb: 'true',
              service: 'storage',
            })
          : undefined;

        if (portalUrl) {
          const appointmentLabel = values.datePreferred
            ? `${values.datePreferred.toLocaleString({
                weekday: 'long',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}${
                values.dateScheduled?.formatted
                  ? ` between ${values.dateScheduled.formatted}`
                  : ''
              }`
            : undefined;
          const passwordState: SetPasswordLocationState = {
            accountUrl: portalUrl,
            email: values.email,
            addressLine: formatAddressLine(values.address),
            appointmentLabel,
          };
          history.push({
            pathname: WWW_ROUTES.STORAGE_SET_PASSWORD,
            state: passwordState,
          });
        } else {
          window.location.assign(
            buildUrlWithQs(portalAccountUrl('appointments'), {
              pb: 'true',
              service: 'storage',
            }),
          );
        }
      }
    } catch (error) {
      if (error instanceof GQLError) {
        if (error.metadata.canResolveViaMFA) {
          setShowMFAModal(true);
          track({ action: 'display', objectName: 'mfa_modal' });
        } else {
          setGqlError(error.fullError);
        }
      } else {
        setGqlError({
          errorMessage:
            'Unable to complete your reservation. Please try again.',
          errorCode: ErrorCodeEnum.Unknown,
        });
      }
    } finally {
      if (!hasBooked) {
        setLoading(false);
      }
    }
  };

  const ErrorText = isDesktop ? Text.Callout : Text.Caption;

  const bookingError = (gqlError || paymentMethodError || quoteError) && (
    <Box textAlign="center" margin="8px 0 0">
      <ErrorText color={COLORS.toucan}>
        {gqlError && <FormattedGQLError error={gqlError} />}
        {paymentMethodError && (
          <PaymentMethodError error={paymentMethodError} />
        )}
        {quoteError && !gqlError && !paymentMethodError && (
          <>Unable to prepare your reservation. Please try again.</>
        )}
      </ErrorText>
    </Box>
  );

  const onboardingCancellationPolicy =
    cancellationPolicyData?.orderCancellationPolicyGroup
      ?.onboardingCancellationPolicy;
  const cancelBy = onboardingCancellationPolicy?.latestFreeCancellationDate
    ? DateTime.fromISO(onboardingCancellationPolicy?.latestFreeCancellationDate)
    : resolveAppointmentDateTime(values).minus({
        days: 2,
      });

  const disableReserveButton =
    loading ||
    quoteLoading ||
    !reserveQuoteReady ||
    reservationSummaryProps.monthlyTotalAmount <= 0;

  const monthlyBaseAmount =
    reservationSummaryProps.storagePlanAmount +
    reservationSummaryProps.protectionPlanAmount;
  const packingBundle = pricingSummary?.bundles?.find(
    (bundle) => bundle.kind === bundleKind,
  );
  const packingAmount =
    packingBundle?.kind === DoorToDoor__BundleEnum.PickupAndPacking
      ? (packingBundle.packageSetEntry?.amount ?? 0)
      : 0;
  const pickupFeeAmount =
    packingAmount +
    (dateScheduled?.feeAmount?.value ?? 0) +
    (pricingSummary?.gasFeeAmount ?? 0) +
    (disposalSelected ? 50 : 0);

  const promoCodeElement = (
    <PromoCodeInput
      coupon={coupon}
      onChange={(value) => {
        onChange('coupon', value);
      }}
      service="storage"
      appearance="challenger"
      monthlyBaseAmount={monthlyBaseAmount}
      pickupFeeAmount={pickupFeeAmount}
    />
  );

  const paymentMethodElement = (
    <Box id={PAYMENT_METHOD_ID}>
      <PaymentMethod
        appearance="challenger"
        disabled={loading}
        defaultSource={defaultSource}
        onClearDefaultSource={() => onChange('defaultSource', undefined)}
      />
    </Box>
  );

  return (
    <>
      <ChallengerConfirmReservation
        values={values}
        reservationSummary={reservationSummaryProps}
        cancelBy={cancelBy}
        bookingError={bookingError}
        buttonDisabled={disableReserveButton}
        buttonLoading={loading}
        quotePreparing={quoteLoading}
        onConfirm={() => onReserve({})}
        onEdit={goToDetailPage}
        onCancellationLinkClick={() => {
          track({
            action: 'click',
            objectName: 'cancellation_policy',
            objectType: 'button',
            label: 'cancellation and booking policy',
          });
          setShowCancellationModal(true);
        }}
        onChangeName={(value) => {
          track({
            action: 'input',
            objectName: 'name',
            value,
            objectType: 'input:text',
            label: 'First & Last',
          });
          onChange('name', value);
        }}
        onChangeEmail={(value) => {
          track({
            action: 'input',
            objectName: 'email',
            value,
            objectType: 'input:text',
            label: 'Email',
          });
          onChange('email', value);
        }}
        onChangePhone={(value) => {
          setPhoneError(undefined);
          onChange('phone', value);
        }}
        phoneError={phoneError}
        promoCodeElement={promoCodeElement}
        paymentMethodElement={paymentMethodElement}
      />
      <MFAModal
        {...leadData}
        zip={zip ?? ''}
        serviceNeeds={SERVICE_NEEDS}
        isOpen={showMFAModal}
        onClose={onMFAModalClose}
      />
      <CancellationPolicyModal
        isOpen={showCancellationModal}
        onClose={() => setShowCancellationModal(false)}
      />
      <MissingInformationModal values={values} variant="challenger" />
    </>
  );
};

const SmartStorageCartShell: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <ChallengerLayout step="confirm_reservation">{children}</ChallengerLayout>
);

export const ChallengerSmartStorageCart = () => (
  <WTProvider
    params={{
      container: StorageCheckoutStep.Cart,
    }}
  >
    <Elements options={DEFAULT_ELEMENTS_OPTIONS} stripe={getStripe()}>
      <PaymentContextProvider>
        <SmartStorageCartShell>
          <CheckoutCartContent />
        </SmartStorageCartShell>
      </PaymentContextProvider>
    </Elements>
  </WTProvider>
);
